import { useMemo } from "react";
import { useSelector } from "react-redux";

import { config, PRODUCT_IDS } from "@chef/constants";
import { isEqualStrings } from "@chef/utils/equal";
import {
  isMealboxBasketType,
  isMealboxProduct,
  isOneSubProduct,
  getDeliveryFeeForWeek,
} from "@chef/helpers";
import { isNotEmptyArray, sumByProperty } from "@chef/utils/array";

import { useLoyalty } from "../hooks";
import { selectDeviationProducts } from "../features";
import { useProductsByIdsQuery } from "../graphql/generated";
import {
  getAttribute,
  getMealsFromVariations,
  isPickAndMixProduct,
} from "../helpers/product";
import { useCalendarData } from "./useCalendarData";
import { compareBasketsProducts } from "../helpers";

/**
 * The purpose of this hook is to give access to commonly used data that is often needed
 * when dealing with the basket. Do not include a bunch of niche stuff in this hook.
 */
export const useExtendedBasketData = (args: {
  week: number;
  year: number;
  skip?: boolean;
}) => {
  const { week, year } = args;

  const skip = !week || !year || args.skip;

  const { basket, calendar } = useCalendarData({
    week,
    year,
    skip,
  });

  const { data: loyaltyData } = useLoyalty();

  const calendarWeek = calendar?.find(
    (cw) => cw.week === week && cw.year === year,
  );

  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const {
    data: productsByIdsQuery,
    isLoading: isLoadingProductsByIds,
    isFetching: isFetchingProductsByIds,
    isError: isErrorProductsByIds,
  } = useProductsByIdsQuery(
    {
      productIds: [PRODUCT_IDS.ONESUB],
      week,
      year,
    },
    { skip },
  );

  const products = productsByIdsQuery?.products;
  const onesubProduct = products?.find(isOneSubProduct);

  const selectedMealbox = selectedProducts?.find(isMealboxProduct);
  const mealboxVariation = onesubProduct?.variations.find((v) =>
    isEqualStrings(v.variationId, selectedMealbox?.variationId),
  );

  const selectedPortions = useMemo(() => {
    if (!mealboxVariation) {
      return config.order.defaultPortions;
    }
    return +(
      getAttribute("Portions", mealboxVariation) || config.order.defaultPortions
    );
  }, [mealboxVariation]);

  const selectedMeals = useMemo(() => {
    if (!mealboxVariation) {
      if (!selectedProducts) {
        return config.order.defaultMeals;
      }
      return selectedProducts.filter(isPickAndMixProduct).length;
    }

    return +(
      getAttribute("Meals", mealboxVariation) || config.order.defaultMeals
    );
  }, [mealboxVariation]);

  const mealsOptionsForSelectedPortions = getMealsFromVariations({
    variations: onesubProduct?.variations,
    portions: selectedPortions,
  });

  const minMealsForSelectedPortions = isNotEmptyArray(
    mealsOptionsForSelectedPortions,
  )
    ? Math.min(...mealsOptionsForSelectedPortions)
    : config.order.minMeals;

  const shippingDetails = calendarWeek?.baskets.find((b) =>
    isMealboxBasketType(b),
  )?.shippingDetails;

  const deliveryInformation = shippingDetails?.plannedDeliveryInformation;

  const isPaused = !shippingDetails?.hasDelivery;
  const isUndeliverable = !deliveryInformation?.isAvailableForDelivery;

  const expectedBasket = [...basket.mealboxBasket, ...basket.groceryBasket];

  const basketHasDeviations = useMemo(
    () => !compareBasketsProducts(expectedBasket, selectedProducts ?? []),
    [expectedBasket, selectedProducts],
  );
  const selectedItems = (selectedProducts ?? []).filter(
    (p) => !isMealboxProduct(p),
  );

  const basketSize = sumByProperty(selectedItems, "quantity");

  const deliveryFee = useMemo(
    () =>
      getDeliveryFeeForWeek({
        calendar: calendar,
        week,
        year,
        loyaltyLevel: loyaltyData?.currentLevel,
      }),
    [calendar, loyaltyData, week, year],
  );

  return {
    /**
     * @property {@param {import('../features').IDeviationProduct[]}} selectedProducts - The currently selected basket products by the user for the given week and year. This deviates from chosenProducts in that this adds deviations to chosenProducts, both additions and subtractions.
     * @property {@param {import('../graphql/generated').ProductsByIdsQuery['products'][number]['variations'][number]}} mealboxVariation - The variation of the mealbox product selected by the user.
     * @property {Object} mealboxProduct - The mealbox product associated with the basket.
     * @property {number[]} mealsOptions - The available meal options for the selected portions.
     * @property {number} minMeals - The minimum number of meals required for the selected portions.
     * @property {number} portions - The number of portions selected by the user.
     * @property {number} meals - The number of meals selected by the user.
     * @property {@param {import('../features').IDeviationProduct[]}} chosenProducts - The currently ordered basket products by the user for the given week and year.
     * @property {boolean} isPaused - Indicates whether the delivery is paused for the given week and year.
     * @property {boolean} isUndeliverable - Indicates whether the delivery is undeliverable for the given week and year.
     * @property {boolean} basketHasDeviations - Indicates whether the basket has deviations from the expected products.
     * @property {number} deliveryFee - The delivery fee for the given week and year, considering loyalty level.
     * @property {number} basketSize - The total quantity of non-mealbox products in the selected basket.
     * @property {@param {import('../graphql/generated').CalendarQuery['calendar'][number]} calendarWeek - The calendar week for the given week and year
     */
    data: {
      selectedProducts,
      mealboxVariation,
      mealboxProduct: onesubProduct,
      mealsOptions: mealsOptionsForSelectedPortions,
      minMeals: minMealsForSelectedPortions,
      portions: selectedPortions,
      meals: selectedMeals,
      chosenProducts: expectedBasket,
      isPaused,
      isUndeliverable,
      basketHasDeviations,
      deliveryFee,
      basketSize,
      calendarWeek,
    },
    isLoading: isLoadingProductsByIds,
    isFetching: isFetchingProductsByIds,
    isError: isErrorProductsByIds,
  };
};
